import React, { useEffect, useState } from "react";
import { Card, Spin, Button } from "antd";
import { GoogleOutlined, StarOutlined, ShareAltOutlined } from "@ant-design/icons";
import Theme from "../../theme/Theme";

function GoogleNewsWidget() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Latest");

  const categories = ["Latest", "Mobile", "Gadgets", "Internet", "Virtual reality", "Artificial intelligence", "Computing"];

  useEffect(() => {
    fetchNews();
  }, [activeCategory]);

  async function fetchNews() {
    setLoading(true);
    try {
      // Fetch from reputable tech news sources
      const query = activeCategory === "Latest" ? "" : `&q=${activeCategory}`;
      const response = await fetch(
        `https://newsapi.org/v2/top-headlines?category=technology${query}&language=en&pageSize=5&apiKey=3962145e9ae94fb588f1a74afc6435ac`
      );
      const data = await response.json();
      
      if (data.articles && data.articles.length > 0) {
        setNews(data.articles);
      } else {
        // Fallback to mock tech news data
        setNews([
          {
            title: "Google unveils Pixel 10a smartphone starting at $499",
            source: { name: "WIRED" },
            description: "Google's Pixel 10a Looks Pretty Familiar",
            author: "Julian Chokkattu",
            publishedAt: new Date(Date.now() - 19 * 60 * 60 * 1000).toISOString(),
            url: "https://news.google.com",
            urlToImage: "https://via.placeholder.com/180x120/4285f4/ffffff?text=Pixel+10a",
            relatedArticles: [
              { source: "BBC", description: "Get your first look at the new Pixel 10a.", time: "16 hours ago" },
              { source: "TechCrunch", description: "Google Store trade-ins towards Pixel are now an instant discount", time: "11 hours ago" }
            ]
          },
          {
            title: "Microsoft announces new AI features for Windows",
            source: { name: "The Verge" },
            description: "Windows gets smarter with new AI integration",
            author: "Tom Warren",
            publishedAt: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
            url: "https://news.google.com",
            urlToImage: "https://via.placeholder.com/180x120/34a853/ffffff?text=Windows+AI",
            relatedArticles: [
              { source: "Reuters", description: "Microsoft integrates AI across Windows platform", time: "12 hours ago" }
            ]
          }
        ]);
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      // Fallback to mock tech news data
      setNews([
        {
          title: "Google unveils Pixel 10a smartphone starting at $499",
          source: { name: "WIRED" },
          description: "Google's Pixel 10a Looks Pretty Familiar",
          author: "Julian Chokkattu",
          publishedAt: new Date(Date.now() - 19 * 60 * 60 * 1000).toISOString(),
          url: "https://news.google.com",
          urlToImage: "https://via.placeholder.com/180x120/4285f4/ffffff?text=Pixel+10a",
          relatedArticles: [
            { source: "BBC", description: "Get your first look at the new Pixel 10a.", time: "16 hours ago" },
            { source: "TechCrunch", description: "Google Store trade-ins towards Pixel are now an instant discount", time: "11 hours ago" }
          ]
        },
        {
          title: "Microsoft announces new AI features for Windows",
          source: { name: "The Verge" },
          description: "Windows gets smarter with new AI integration",
          author: "Tom Warren",
          publishedAt: new Date(Date.now() - 13 * 60 * 60 * 1000).toISOString(),
          url: "https://news.google.com",
          urlToImage: "https://via.placeholder.com/180x120/34a853/ffffff?text=Windows+AI",
          relatedArticles: [
            { source: "Reuters", description: "Microsoft integrates AI across Windows platform", time: "12 hours ago" }
          ]
        },
        {
          title: "Apple's new MacBook Pro features breakthrough performance",
          source: { name: "TechCrunch" },
          description: "Latest MacBook Pro sets new standards",
          author: "Brian Heater",
          publishedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          url: "https://news.google.com",
          urlToImage: "https://via.placeholder.com/180x120/ea4335/ffffff?text=MacBook",
          relatedArticles: [
            { source: "CNET", description: "Apple MacBook Pro review: A powerhouse laptop", time: "2 days ago" }
          ]
        }
      ]);
    } finally {
      setLoading(false);
    }
  }

  function getTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  }

  return (
    <Card
      className="border rounded-md"
      bodyStyle={{ padding: 0, maxHeight: '700px', display: 'flex', flexDirection: 'column' }}
      style={{ backgroundColor: Theme().attentionLight, color: Theme().textPrimary, maxHeight: '700px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ flexShrink: 0, borderColor: Theme().navBorder }}>
        <div className="flex items-center gap-2">
          <GoogleOutlined className="text-xl" style={{ color: Theme().primary }} />
          <span className="font-semibold" style={{ color: Theme().textPrimary }}>Technology</span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            type="text" 
            icon={<StarOutlined />} 
            className="hover:opacity-80"
            style={{ color: Theme().textPrimary }}
            size="small"
          >
            Follow
          </Button>
          <Button 
            type="text" 
            icon={<ShareAltOutlined />} 
            className="hover:opacity-80"
            style={{ color: Theme().textPrimary }}
            size="small"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 p-4 border-b overflow-x-auto" style={{ flexShrink: 0, borderColor: Theme().navBorder }}>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors`}
            style={{
              backgroundColor: activeCategory === category ? Theme().primary : 'rgba(241, 90, 36, 0.1)',
              color: activeCategory === category ? Theme().textWhite : Theme().textPrimary,
            }}
          >
            {category}
          </button>
        ))}
      </div>

      {/* News Content - Scrollable */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Spin />
          </div>
        ) : (
          <div className="p-4">
            {news.map((article, index) => (
              <div key={index} className="mb-6 pb-6 border-b last:border-b-0" style={{ borderColor: Theme().navBorder }}>
                {/* Main Article */}
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:opacity-80 transition-opacity"
                >
                  <div className="flex gap-4 mb-3">
                    {article.urlToImage && (
                      <img
                        src={article.urlToImage}
                        alt={article.title}
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        className="w-44 h-28 object-cover rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1">
                      <div className="text-xs mb-1 flex items-center gap-2" style={{ color: Theme().textPrimary, opacity: 0.7 }}>
                        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(241, 90, 36, 0.1)' }}>{article.source?.name}</span>
                      </div>
                      <h3 className="font-medium mb-2 leading-tight" style={{ color: Theme().textPrimary }}>
                        {article.title}
                      </h3>
                      <div className="text-xs" style={{ color: Theme().textPrimary, opacity: 0.6 }}>
                        {getTimeAgo(article.publishedAt)} · By {article.author || "Staff"}
                      </div>
                    </div>
                  </div>
                </a>

                {/* Related Articles */}
                {article.relatedArticles && article.relatedArticles.length > 0 && (
                  <div className="ml-0 mt-3 space-y-2">
                    {article.relatedArticles.map((related, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <div className="px-2 py-0.5 rounded text-xs whitespace-nowrap" style={{ backgroundColor: 'rgba(241, 90, 36, 0.1)', color: Theme().textPrimary, opacity: 0.7 }}>
                          {related.source}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm" style={{ color: Theme().textPrimary }}>{related.description}</p>
                          <span className="text-xs" style={{ color: Theme().textPrimary, opacity: 0.5 }}>{related.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* See more link */}
                {article.relatedArticles && article.relatedArticles.length > 0 && (
                  <button className="mt-3 text-sm hover:opacity-80 flex items-center gap-1" style={{ color: Theme().primary }}>
                    <span>📰</span>
                    <span>See more headlines & perspectives</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 text-center border-t" style={{ flexShrink: 0, borderColor: Theme().navBorder }}>
        <a
          href="https://news.google.com/topics/CAAqJggKIiBDQkFTRWdvSUwyMHZNRGRqTVhZU0FtVnVHZ0pWVXlnQVAB"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm hover:opacity-80"
          style={{ color: Theme().primary }}
        >
          View full coverage on Google News →
        </a>
      </div>
    </Card>
  );
}

export default GoogleNewsWidget;
